import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import MovieCard from '@/components/feature/MovieCard';
import { movieCategories, getMoviesByCategory } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  const currentCategory = movieCategories.find((c) => c.slug === slug);
  const movies = useMemo(() => {
    const list = slug ? getMoviesByCategory(slug) : [];
    if (sortBy === 'rating') return [...list].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'year') return [...list].sort((a, b) => b.year - a.year);
    return list;
  }, [slug, sortBy]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <p className="text-foreground-400">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const categoryLabel = currentCategory.name;

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${categoryLabel}在线观看 - 天美影视`,
    description: `天美影视${categoryLabel}专区，提供最新${categoryLabel}高清免费在线观看，不缓冲流畅追大片。`,
    url: `https://www.tianmeiying.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '天美影视',
      url: 'https://www.tianmeiying.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tianmeiying.com/' },
      { '@type': 'ListItem', position: 2, name: '电影分类', item: 'https://www.tianmeiying.com/category/dongzuo' },
      { '@type': 'ListItem', position: 3, name: categoryLabel },
    ],
  };

  const sortOptions: { value: typeof sortBy; label: string }[] = [
    { value: 'default', label: '默认排序' },
    { value: 'rating', label: '评分最高' },
    { value: 'year', label: '最新发布' },
  ];

  return (
    <>
      <SchemaOrg data={webPageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <Navbar />
      <ScrollToTop />
      <main>
        <article className="bg-background-200 min-h-screen">
          <div className="w-full px-4 md:px-6 pt-24 pb-10">
            <div className="max-w-[1200px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '电影分类', href: '/category/dongzuo' },
                  { label: categoryLabel },
                ]}
              />

              <div className="flex items-center gap-3 mb-6 animate-fade-in-up animation-fill-both">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/10">
                  <i className={`${currentCategory.icon} text-xl text-primary-400`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100">
                    {categoryLabel}在线观看
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    共 <strong>{currentCategory.count}</strong> 部{categoryLabel}资源
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs text-foreground-600 mr-2">分类筛选：</span>
                {movieCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-background-700/50 text-foreground-400 hover:text-foreground-200 hover:bg-background-600/50'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center justify-between mb-6">
                <div></div>
                <div className="flex items-center gap-1 bg-background-700/40 rounded-full p-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === opt.value
                          ? 'bg-primary-500 text-background-950'
                          : 'text-foreground-500 hover:text-foreground-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {movies.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-film-line text-5xl text-foreground-700"></i>
                  <p className="text-foreground-500 mt-4">暂无相关电影资源</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-product-shop="">
                  {movies.map((movie, i) => (
                    <div key={movie.id} className="animate-fade-in-up animation-fill-both" style={{ animationDelay: `${i * 0.05}s` }}>
                      <MovieCard movie={movie} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}